import { formatActivityDate } from '@/dsm-components';
import { type EquipmentBrand } from '@/services/equipmentManufacturers';
import { PalmettoButton, PalmettoIcon, PalmettoTag } from '@palmetto/dsm-components';
import { useCallback } from 'react';

import { EquipmentRecordStatus } from 'types';

import { emptyValueLabel, EquipmentRecordStatusLabels, EquipmentRecordStatusTagColors } from './equipmentLabels';

interface ExternalLinkCellProps {
  url?: string;
  label: string;
}

/** Most brands are missing at least one URL, so the cell shows a placeholder over an empty anchor. */
const ExternalLinkCell = ({ url, label }: ExternalLinkCellProps) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" aria-label={label} className="text-text-primary">
      <PalmettoIcon name="link" size="sm" />
    </a>
  ) : (
    <span className="text-text-tertiary">{emptyValueLabel}</span>
  );

export interface EquipmentBrandRowProps {
  brand: EquipmentBrand;
  canEdit: boolean;
  /** True while any row on the section is open for editing; only one editor runs at a time. */
  isEditingAny: boolean;
  onEdit: (brand: EquipmentBrand) => void;
  onDeactivate: (brand: EquipmentBrand) => void;
  onActivate: (brand: EquipmentBrand) => void;
}

export const EquipmentBrandRow = ({
  brand,
  canEdit,
  isEditingAny,
  onEdit,
  onDeactivate,
  onActivate,
}: EquipmentBrandRowProps) => {
  const handleEdit = useCallback(() => onEdit(brand), [onEdit, brand]);
  const handleDeactivate = useCallback(() => onDeactivate(brand), [onDeactivate, brand]);
  const handleActivate = useCallback(() => onActivate(brand), [onActivate, brand]);

  const isActive = brand.status === EquipmentRecordStatus.active;

  return (
    <tr className="border-b border-border-default">
      <td className="p-8 body-300 text-text-primary">{brand.name}</td>
      <td className="p-8">
        <ExternalLinkCell url={brand.websiteUrl} label={`${brand.name} website`} />
      </td>
      <td className="p-8">
        <ExternalLinkCell url={brand.warrantyRegistrationUrl} label={`${brand.name} warranty registration`} />
      </td>
      <td className="p-8">
        <PalmettoTag
          label={EquipmentRecordStatusLabels[brand.status] ?? brand.status}
          color={EquipmentRecordStatusTagColors[brand.status]}
        />
      </td>
      <td className="p-8 body-300 text-text-secondary">
        {brand.meta?.modifiedAt ? formatActivityDate(brand.meta.modifiedAt) : emptyValueLabel}
        {brand.modifiedByName ? ` by ${brand.modifiedByName}` : ''}
      </td>
      <td className="p-8">
        {canEdit && (
          <div className="flex justify-end gap-8">
            <PalmettoButton
              type="button"
              emphasis="secondary"
              layout="hug"
              aria-label={`Edit ${brand.name}`}
              onClick={handleEdit}
              disabled={isEditingAny}
            >
              Edit
            </PalmettoButton>
            {isActive ? (
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                aria-label={`Deactivate ${brand.name}`}
                onClick={handleDeactivate}
                disabled={isEditingAny}
              >
                Deactivate
              </PalmettoButton>
            ) : (
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                aria-label={`Activate ${brand.name}`}
                onClick={handleActivate}
                disabled={isEditingAny}
              >
                Activate
              </PalmettoButton>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
